import type { Ref } from 'vue'
import { onUnmounted, ref } from 'vue'

export interface UseBottomOverscrollOptions {
  /** 容器元素 */
  containerRef: Ref<HTMLElement | undefined>
  /** 是否已到达底部 */
  isAtBottom: Ref<boolean>
  /** 是否启用（可用于条件判断，如是否是最后一章） */
  enabled: Ref<boolean>
  /** 过度滚动阈值，默认 80px */
  threshold?: number
  /** 触发回调 */
  onTrigger: () => void
}

/**
 * 底部过度滚动检测
 * 当页面已在底部时，用户继续向下拉超过阈值才触发回调
 */
export function useBottomOverscroll(options: UseBottomOverscrollOptions) {
  const {
    containerRef,
    isAtBottom,
    enabled,
    threshold = 80,
    onTrigger,
  } = options

  const overscrollDistance = ref(0)
  let lastTouchY = 0

  function onTouchStart(e: TouchEvent) {
    if (e.touches.length)
      lastTouchY = e.touches[0].clientY
  }

  function onTouchMove(e: TouchEvent) {
    if (!enabled.value || !isAtBottom.value || !e.touches.length)
      return

    const currentY = e.touches[0].clientY
    const delta = lastTouchY - currentY // 向上滑为正（内容向下滚）

    if (delta > 0) {
      overscrollDistance.value += delta
      if (overscrollDistance.value >= threshold) {
        onTrigger()
        overscrollDistance.value = 0
      }
    }
    else {
      overscrollDistance.value = 0 // 向下滑时重置
    }
    lastTouchY = currentY
  }

  function onTouchEnd() {
    overscrollDistance.value = 0
  }

  // PC 端滚轮支持
  function onWheel(e: WheelEvent) {
    if (!enabled.value || !isAtBottom.value)
      return

    if (e.deltaY > 0) {
      overscrollDistance.value += e.deltaY
      if (overscrollDistance.value >= threshold) {
        onTrigger()
        overscrollDistance.value = 0
      }
    }
    else {
      overscrollDistance.value = 0
    }
  }

  function startListen() {
    const container = containerRef.value
    if (!container)
      return

    container.addEventListener('touchstart', onTouchStart)
    container.addEventListener('touchmove', onTouchMove)
    container.addEventListener('touchend', onTouchEnd)
    container.addEventListener('wheel', onWheel)
  }

  function stopListen() {
    const container = containerRef.value
    if (!container)
      return

    container.removeEventListener('touchstart', onTouchStart)
    container.removeEventListener('touchmove', onTouchMove)
    container.removeEventListener('touchend', onTouchEnd)
    container.removeEventListener('wheel', onWheel)
  }

  function reset() {
    overscrollDistance.value = 0
  }

  onUnmounted(() => {
    stopListen()
  })

  return {
    overscrollDistance,
    startListen,
    stopListen,
    reset,
  }
}
